import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, Upload, FileText, Camera, Sparkles, Bell, User, Star,
  Shield, Clock, Zap, CheckCircle, ChevronRight, ChevronLeft,
  Play, Pause, RotateCcw, X, TrendingUp, Gavel, Users, Award,
  MapPin, Euro, BedDouble, Bath, Square, Image, Video, FileCheck,
  AlertCircle, Send, MessageSquare, Phone, Mail, Building, Heart
} from 'lucide-react';

const DemoPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [animationPhase, setAnimationPhase] = useState(0);
  const intervalRef = useRef(null);

  const steps = [
    { 
      id: 'property-insert', 
      title: 'Inserimento Immobile', 
      subtitle: 'Il proprietario inserisce il suo immobile',
      player: 'Proprietario',
      icon: Home,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      id: 'documents', 
      title: 'Gestione Documenti', 
      subtitle: 'Caricamento e verifica documentazione',
      player: 'Proprietario',
      icon: FileText,
      color: 'from-blue-500 to-cyan-500'
    },
    { 
      id: 'virtual-tour', 
      title: 'Virtual Tour AI', 
      subtitle: 'Creazione automatica del tour virtuale',
      player: 'Sistema AI',
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500'
    },
    { 
      id: 'agent-notification', 
      title: 'Notifica Agente', 
      subtitle: "L'agente riceve la richiesta del venditore",
      player: 'Agente',
      icon: Bell,
      color: 'from-violet-500 to-purple-500'
    },
    { 
      id: 'agent-cards', 
      title: 'Selezione Agente', 
      subtitle: 'WEQASA consiglia gli agenti migliori',
      player: 'Sistema',
      icon: Award,
      color: 'from-emerald-500 to-teal-500'
    },
    { 
      id: 'auction', 
      title: 'Gara di Vendita', 
      subtitle: 'Gli acquirenti competono con offerte al rialzo',
      player: 'Acquirente',
      icon: Gavel,
      color: 'from-amber-500 to-orange-500'
    }
  ];

  // Auto-play animation phases
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setAnimationPhase(prev => {
          if (prev >= 4) {
            // Move to next step
            setCurrentStep(current => {
              if (current >= steps.length - 1) {
                setIsPlaying(false);
                return current;
              }
              return current + 1;
            });
            return 0;
          }
          return prev + 1;
        });
      }, 2000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, steps.length]);

  // Reset animation phase when step changes manually
  useEffect(() => {
    if (!isPlaying) {
      setAnimationPhase(0);
    }
  }, [currentStep, isPlaying]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setAnimationPhase(0);
    setIsPlaying(true);
  };

  const handleStepClick = (index) => {
    setCurrentStep(index);
    setAnimationPhase(0);
    setIsPlaying(false);
  };

  // Render different demo screens based on current step
  const renderDemoScreen = () => {
    switch (steps[currentStep].id) {
      case 'property-insert':
        return <PropertyInsertDemo phase={animationPhase} />;
      case 'documents':
        return <DocumentsDemo phase={animationPhase} />;
      case 'virtual-tour':
        return <VirtualTourDemo phase={animationPhase} />;
      case 'agent-notification':
        return <AgentNotificationDemo phase={animationPhase} />;
      case 'agent-cards':
        return <AgentCardsDemo phase={animationPhase} />;
      case 'auction':
        return <AuctionDemo phase={animationPhase} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-white">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/weqasa-logo.png" alt="WEQASA" className="h-8 w-auto brightness-0 invert" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white">Demo Interattiva</span>
            <Link 
              to="/investors" 
              className="px-4 py-2 bg-white/10 rounded-full text-sm font-medium hover:bg-white/20 transition flex items-center gap-2"
            >
              <X className="w-4 h-4" />
              Chiudi Demo
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-white">
              Scopri come funziona WEQASA
            </h1>
            <p className="text-white/80">
              Una piattaforma user-friendly per venditori, agenti e acquirenti
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex justify-center mb-8 overflow-x-auto pb-4">
            <div className="flex gap-2 sm:gap-4">
              {steps.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => handleStepClick(index)}
                  className={`flex flex-col items-center p-3 rounded-xl transition-all min-w-[100px] ${
                    currentStep === index 
                      ? 'bg-[#0071e3] scale-105' 
                      : currentStep > index 
                        ? 'bg-white/10' 
                        : 'bg-white/5 opacity-60'
                  }`}
                >
                  <step.icon className={`w-5 h-5 mb-1 ${currentStep === index ? 'text-white' : 'text-white/70'}`} />
                  <span className="text-xs font-medium text-center text-white">{step.title}</span>
                  <span className={`text-[10px] mt-1 px-2 py-0.5 rounded-full ${
                    currentStep === index ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'
                  }`}>
                    {step.player}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Demo Area */}
          <div className="relative">
            {/* Demo Screen Container */}
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0d0d0d] rounded-3xl border border-white/10 overflow-hidden shadow-2xl">
              {/* Browser-like header */}
              <div className="bg-[#2a2a2a] px-4 py-3 flex items-center gap-3 border-b border-white/10">
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 bg-[#1a1a1a] rounded-lg px-4 py-1.5 text-sm text-white/50">
                  weqasa.it/{steps[currentStep].id.replace('-', '/')}
                </div>
              </div>

              {/* Demo Content */}
              <div className="p-6 min-h-[500px] relative">
                {renderDemoScreen()}
              </div>
            </div>

            {/* Step Info Card */}
            <div className={`absolute -bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r ${steps[currentStep].color} px-6 py-3 rounded-full shadow-lg`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                  {React.createElement(steps[currentStep].icon, { className: "w-4 h-4 text-white" })}
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{steps[currentStep].title}</p>
                  <p className="text-xs text-white/80">{steps[currentStep].subtitle}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="flex justify-center items-center gap-4 mt-16">
            <button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            <button
              onClick={handlePlayPause}
              className="px-8 py-4 bg-[#0071e3] rounded-full font-semibold hover:bg-[#0077ED] transition flex items-center gap-3"
            >
              {isPlaying ? (
                <>
                  <Pause className="w-5 h-5" />
                  Pausa
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Avvia Demo
                </>
              )}
            </button>

            <button
              onClick={handleRestart}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition"
            >
              <RotateCcw className="w-6 h-6 text-white" />
            </button>

            <button
              onClick={() => setCurrentStep(Math.min(steps.length - 1, currentStep + 1))}
              disabled={currentStep === steps.length - 1}
              className="p-3 bg-white/10 rounded-full hover:bg-white/20 transition disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-3 gap-6 mt-16 max-w-3xl mx-auto">
            <div className="text-center">
              <div className="w-12 h-12 bg-[#0071e3]/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Zap className="w-6 h-6 text-[#0071e3]" />
              </div>
              <p className="font-semibold text-white">Rapidità</p>
              <p className="text-sm text-white/60">Processo semplificato</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <p className="font-semibold text-white">Sicurezza</p>
              <p className="text-sm text-white/60">Transazioni protette</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
              <p className="font-semibold text-white">Efficienza</p>
              <p className="text-sm text-white/60">Risultati migliori</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ DEMO COMPONENTS ============

// 1. Property Insert Demo
const PropertyInsertDemo = ({ phase }) => {
  return (
    <div className="grid md:grid-cols-2 gap-8">
      {/* Left - Form */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold flex items-center gap-2 text-white">
          <Home className="w-5 h-5 text-[#0071e3]" />
          Inserisci il tuo immobile
        </h2>
        
        <div className={`space-y-4 transition-all duration-500 ${phase >= 0 ? 'opacity-100' : 'opacity-30'}`}>
          <div>
            <label className="text-sm text-white/70 mb-1 block">Indirizzo</label>
            <div className={`bg-white/5 border border-white/10 rounded-xl p-3 transition-all ${phase >= 1 ? 'border-[#0071e3] bg-[#0071e3]/10' : ''}`}>
              <span className={`text-white ${phase >= 1 ? '' : 'opacity-0'}`}>
                Via Roma 42, Milano
                {phase === 1 && <span className="animate-pulse">|</span>}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm text-white/70 mb-1 block">Locali</label>
              <div className={`bg-white/5 border border-white/10 rounded-xl p-3 text-center transition-all ${phase >= 2 ? 'border-[#0071e3]' : ''}`}>
                <span className={`text-white font-bold ${phase >= 2 ? '' : 'opacity-0'}`}>3</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Bagni</label>
              <div className={`bg-white/5 border border-white/10 rounded-xl p-3 text-center transition-all ${phase >= 2 ? 'border-[#0071e3]' : ''}`}>
                <span className={`text-white font-bold ${phase >= 2 ? '' : 'opacity-0'}`}>2</span>
              </div>
            </div>
            <div>
              <label className="text-sm text-white/70 mb-1 block">Mq</label>
              <div className={`bg-white/5 border border-white/10 rounded-xl p-3 text-center transition-all ${phase >= 2 ? 'border-[#0071e3]' : ''}`}>
                <span className={`text-white font-bold ${phase >= 2 ? '' : 'opacity-0'}`}>95</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm text-white/70 mb-1 block">Prezzo base richiesto</label>
            <div className={`bg-white/5 border border-white/10 rounded-xl p-3 transition-all ${phase >= 3 ? 'border-[#0071e3] bg-[#0071e3]/10' : ''}`}>
              <span className={`text-2xl font-bold text-[#0071e3] ${phase >= 3 ? '' : 'opacity-0'}`}>€ 285.000</span>
            </div>
          </div>
        </div>

        <button className={`w-full py-3 rounded-xl font-semibold transition-all ${phase >= 4 ? 'bg-[#0071e3] text-white scale-105' : 'bg-white/10 text-white/50'}`}>
          {phase >= 4 ? '✓ Immobile Pubblicato!' : 'Pubblica Immobile'}
        </button>
      </div>

      {/* Right - Photo Upload */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-white">
          <Camera className="w-5 h-5 text-[#0071e3]" />
          Foto dell'immobile
        </h3>
        
        <div className="grid grid-cols-2 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div 
              key={i}
              className={`aspect-video bg-white/5 border border-white/10 rounded-xl flex items-center justify-center transition-all duration-500 ${
                phase >= i ? 'border-[#0071e3] bg-gradient-to-br from-[#0071e3]/20 to-transparent' : ''
              }`}
            >
              {phase >= i ? (
                <div className="text-center">
                  <CheckCircle className="w-8 h-8 text-[#0071e3] mx-auto mb-1" />
                  <span className="text-xs text-white/70">Foto {i}</span>
                </div>
              ) : (
                <Upload className="w-8 h-8 text-white/30" />
              )}
            </div>
          ))}
        </div>

        {phase >= 4 && (
          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-xl p-4 animate-pulse">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="font-semibold text-emerald-400">Immobile pubblicato con successo!</p>
                <p className="text-sm text-white/70">Gli agenti della tua zona sono stati notificati</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// 2. Documents Demo
const DocumentsDemo = ({ phase }) => {
  const documents = [
    { name: 'Atto di proprietà', type: 'PDF', status: phase >= 1 ? 'verified' : 'pending' },
    { name: 'Planimetria catastale', type: 'PDF', status: phase >= 2 ? 'verified' : 'pending' },
    { name: 'APE - Certificazione energetica', type: 'PDF', status: phase >= 3 ? 'verified' : 'pending' },
    { name: 'Visura camerale', type: 'PDF', status: phase >= 4 ? 'verified' : 'pending' },
  ];

  return (
    <div className="max-w-2xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-xl font-bold flex items-center justify-center gap-2 text-white">
          <FileText className="w-5 h-5 text-[#0071e3]" />
          Gestione Documenti Sicura
        </h2>
        <p className="text-white/60 mt-2">Carica e verifica tutta la documentazione in un unico posto</p>
      </div>

      <div className="space-y-3">
        {documents.map((doc, index) => (
          <div 
            key={index}
            className={`bg-white/5 border rounded-xl p-4 flex items-center justify-between transition-all duration-500 ${
              doc.status === 'verified' ? 'border-emerald-500/50 bg-emerald-500/10' : 'border-white/10'
            }`}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                doc.status === 'verified' ? 'bg-emerald-500/20' : 'bg-white/10'
              }`}>
                <FileCheck className={`w-6 h-6 ${doc.status === 'verified' ? 'text-emerald-400' : 'text-white/50'}`} />
              </div>
              <div>
                <p className="font-medium text-white">{doc.name}</p>
                <p className="text-sm text-white/50">{doc.type} • Caricato</p>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              doc.status === 'verified' 
                ? 'bg-emerald-500/20 text-emerald-400' 
                : 'bg-yellow-500/20 text-yellow-400'
            }`}>
              {doc.status === 'verified' ? '✓ Verificato' : 'In verifica...'}
            </div>
          </div>
        ))}
      </div>

      {phase >= 4 && (
        <div className="mt-6 bg-[#0071e3]/20 border border-[#0071e3]/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#0071e3]" />
            <div>
              <p className="font-semibold text-white">Documentazione completa e verificata</p>
              <p className="text-sm text-white/70">Tutti i documenti sono stati validati dal sistema WEQASA</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 3. Virtual Tour Demo
const VirtualTourDemo = ({ phase }) => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold flex items-center justify-center gap-2 text-white">
          <Sparkles className="w-5 h-5 text-purple-400" />
          Virtual Tour AI
        </h2>
        <p className="text-white/60 mt-2">L'intelligenza artificiale crea automaticamente il tour virtuale</p>
      </div>

      <div className="relative bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-white/70">Elaborazione AI in corso...</span>
            <span className="text-purple-400 font-bold">{Math.min(phase * 25, 100)}%</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-1000"
              style={{ width: `${Math.min(phase * 25, 100)}%` }}
            />
          </div>
        </div>

        {/* AI Steps */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: Image, label: 'Analisi foto', done: phase >= 1 },
            { icon: Building, label: 'Ricostruzione 3D', done: phase >= 2 },
            { icon: Video, label: 'Rendering tour', done: phase >= 3 },
            { icon: Sparkles, label: 'Ottimizzazione', done: phase >= 4 },
          ].map((step, i) => (
            <div key={i} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${
              step.done ? 'bg-purple-500/20' : 'bg-white/5'
            }`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                step.done ? 'bg-purple-500' : 'bg-white/10'
              }`}>
                {step.done ? (
                  <CheckCircle className="w-5 h-5 text-white" />
                ) : (
                  <step.icon className="w-5 h-5 text-white/50" />
                )}
              </div>
              <span className={step.done ? 'text-white font-medium' : 'text-white/50'}>{step.label}</span>
            </div>
          ))}
        </div>

        {/* Preview */}
        <div className="aspect-video bg-black/50 rounded-xl flex items-center justify-center relative overflow-hidden">
          {phase >= 4 ? (
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-purple-500/20 animate-pulse" />
          ) : null}
          <div className="text-center z-10">
            {phase >= 4 ? (
              <>
                <Video className="w-16 h-16 text-purple-400 mx-auto mb-3" />
                <p className="text-white font-semibold">Virtual Tour Pronto!</p>
                <button className="mt-3 px-6 py-2 bg-purple-500 rounded-full text-sm font-medium hover:bg-purple-600 transition">
                  Visualizza Tour 360°
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-white/70">Generazione in corso...</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// 4. Agent Notification Demo
const AgentNotificationDemo = ({ phase }) => {
  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold flex items-center justify-center gap-2 text-white">
          <Bell className="w-5 h-5 text-violet-400" />
          Dashboard Agente
        </h2>
        <p className="text-white/60 mt-2">L'agente riceve notifiche in tempo reale</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="bg-white/5 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-3 p-2 bg-[#0071e3]/20 rounded-lg">
            <Home className="w-5 h-5 text-[#0071e3]" />
            <span className="text-sm font-medium text-white">I miei immobili</span>
          </div>
          <div className="flex items-center gap-3 p-2 rounded-lg relative">
            <Bell className="w-5 h-5 text-white/50" />
            <span className="text-sm text-white/70">Notifiche</span>
            {phase >= 1 && (
              <span className="absolute right-2 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center animate-bounce">
                1
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 p-2 rounded-lg">
            <MessageSquare className="w-5 h-5 text-white/50" />
            <span className="text-sm text-white/70">Messaggi</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="md:col-span-2 space-y-4">
          {/* Notification Popup */}
          {phase >= 1 && (
            <div className={`bg-gradient-to-r from-violet-500/20 to-purple-500/20 border border-violet-500/30 rounded-xl p-4 transition-all duration-500 ${
              phase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
            }`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-violet-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-white">Nuova richiesta di vendita!</p>
                  <p className="text-sm text-white/70 mt-1">
                    Mario Rossi vuole vendere il suo trilocale in Via Roma 42, Milano
                  </p>
                  <div className="flex gap-2 mt-3">
                    <button className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      phase >= 3 ? 'bg-emerald-500 text-white' : 'bg-[#0071e3] text-white hover:bg-[#0077ED]'
                    }`}>
                      {phase >= 3 ? '✓ Incarico accettato' : 'Accetta incarico'}
                    </button>
                    {phase < 3 && (
                      <button className="px-4 py-2 bg-white/10 rounded-lg text-sm text-white/70">
                        Vedi dettagli
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Property Preview */}
          {phase >= 2 && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <div className="flex gap-4">
                <div className="w-24 h-24 bg-gradient-to-br from-[#0071e3]/20 to-purple-500/20 rounded-xl flex items-center justify-center">
                  <Home className="w-10 h-10 text-[#0071e3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Trilocale Via Roma 42</h3>
                  <p className="text-sm text-white/70 flex items-center gap-1 mt-1">
                    <MapPin className="w-4 h-4" /> Milano, Lombardia
                  </p>
                  <div className="flex gap-4 mt-2 text-sm text-white/60">
                    <span className="flex items-center gap-1"><BedDouble className="w-4 h-4" /> 3</span>
                    <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> 2</span>
                    <span className="flex items-center gap-1"><Square className="w-4 h-4" /> 95 mq</span>
                  </div>
                  <p className="text-lg font-bold text-[#0071e3] mt-2">€ 285.000</p>
                </div>
              </div>
            </div>
          )}

          {/* Contact Info */}
          {phase >= 4 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
              <p className="text-sm font-medium text-emerald-400 mb-3">Contatto proprietario sbloccato</p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-white/80">
                  <Phone className="w-4 h-4" /> +39 333 123 4567
                </div>
                <div className="flex items-center gap-2 text-white/80">
                  <Mail className="w-4 h-4" /> mario.rossi@email.it
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// 5. Agent Cards Demo
const AgentCardsDemo = ({ phase }) => {
  const agents = [
    { 
      name: 'Laura Bianchi', 
      agency: 'ERA Immobiliare Milano',
      rating: 4.9, 
      reviews: 127, 
      sales: 45,
      speed: 32,
      recommended: true 
    },
    { 
      name: 'Marco Verdi', 
      agency: 'WEQASA Partner',
      rating: 4.7, 
      reviews: 89, 
      sales: 38,
      speed: 45,
      recommended: false 
    },
    { 
      name: 'Sofia Russo', 
      agency: 'RE/MAX Excellence',
      rating: 4.8, 
      reviews: 156, 
      sales: 62,
      speed: 28,
      recommended: true 
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold flex items-center justify-center gap-2 text-white">
          <Award className="w-5 h-5 text-emerald-400" />
          Agenti Consigliati da WEQASA
        </h2>
        <p className="text-white/60 mt-2">Selezioniamo i migliori professionisti per rapidità, efficienza e sicurezza</p>
      </div>

      {/* Quality Badges */}
      <div className="flex justify-center gap-4 mb-6">
        {[
          { icon: Zap, label: 'Rapidità', color: 'text-yellow-400' },
          { icon: TrendingUp, label: 'Efficienza', color: 'text-emerald-400' },
          { icon: Shield, label: 'Sicurezza', color: 'text-blue-400' },
        ].map((badge, i) => (
          <div key={i} className={`flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full transition-all duration-500 ${
            phase >= i + 1 ? 'opacity-100 scale-100' : 'opacity-30 scale-95'
          }`}>
            <badge.icon className={`w-4 h-4 ${badge.color}`} />
            <span className="text-sm text-white/80">{badge.label}</span>
          </div>
        ))}
      </div>

      {/* Agent Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {agents.map((agent, index) => (
          <div 
            key={index}
            className={`bg-white/5 border rounded-2xl p-5 transition-all duration-500 ${
              phase >= index + 1 
                ? agent.recommended 
                  ? 'border-emerald-500/50 bg-emerald-500/5 opacity-100 translate-y-0' 
                  : 'border-white/10 opacity-100 translate-y-0'
                : 'opacity-0 translate-y-4'
            }`}
          >
            {agent.recommended && (
              <div className="flex items-center gap-1 text-emerald-400 text-xs font-medium mb-3">
                <CheckCircle className="w-4 h-4" />
                Consigliato da WEQASA
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-gradient-to-br from-[#0071e3] to-purple-500 rounded-xl flex items-center justify-center text-xl font-bold">
                {agent.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <h3 className="font-semibold text-white">{agent.name}</h3>
                <p className="text-sm text-white/60">{agent.agency}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-4">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-4 h-4 ${i < Math.floor(agent.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-white/20'}`} />
                ))}
              </div>
              <span className="text-sm font-bold text-white">{agent.rating}</span>
              <span className="text-sm text-white/50">({agent.reviews} recensioni)</span>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <p className="text-xl font-bold text-[#0071e3]">{agent.sales}</p>
                <p className="text-white/50 text-xs">Vendite</p>
              </div>
              <div className="bg-white/5 rounded-lg p-2 text-center">
                <p className="text-xl font-bold text-emerald-400">{agent.speed}gg</p>
                <p className="text-white/50 text-xs">Tempo medio</p>
              </div>
            </div>

            <button className={`w-full mt-4 py-2 rounded-xl text-sm font-medium transition-all ${
              agent.recommended 
                ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}>
              Contatta Agente
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// 6. Auction Demo
const AuctionDemo = ({ phase }) => {
  const [bidAmount, setBidAmount] = useState(285000);
  
  useEffect(() => {
    if (phase >= 2) setBidAmount(292000);
    if (phase >= 3) setBidAmount(298000);
    if (phase >= 4) setBidAmount(305000);
  }, [phase]);

  const bids = [
    { user: 'Tu', amount: phase >= 4 ? 305000 : 0, time: 'Adesso', isYou: true },
    { user: 'Acquirente B', amount: 298000, time: '2 min fa', isYou: false },
    { user: 'Acquirente C', amount: 292000, time: '5 min fa', isYou: false },
    { user: 'Acquirente B', amount: 288000, time: '8 min fa', isYou: false },
  ].filter(b => b.amount > 0 || phase >= 4);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="text-xl font-bold flex items-center justify-center gap-2 text-white">
          <Gavel className="w-5 h-5 text-amber-400" />
          Gara di Vendita in Corso
        </h2>
        <p className="text-white/60 mt-2">Trasparenza totale - Vedi tutte le offerte in tempo reale</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Property Card */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
          <div className="aspect-video bg-gradient-to-br from-[#0071e3]/20 to-purple-500/20 rounded-xl mb-4 flex items-center justify-center">
            <Home className="w-16 h-16 text-[#0071e3]" />
          </div>
          <h3 className="text-lg font-semibold text-white">Trilocale Via Roma 42</h3>
          <p className="text-sm text-white/60 flex items-center gap-1 mt-1">
            <MapPin className="w-4 h-4" /> Milano, Lombardia
          </p>
          
          <div className="mt-4 p-4 bg-white/5 rounded-xl">
            <div className="flex justify-between items-center">
              <span className="text-white/60">Prezzo base</span>
              <span className="text-white/80">€ 285.000</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-white/60">Offerta attuale</span>
              <span className="text-2xl font-bold text-[#0071e3]">€ {bidAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-white/60">Incremento</span>
              <span className="text-emerald-400 font-semibold">+{((bidAmount - 285000) / 285000 * 100).toFixed(1)}%</span>
            </div>
          </div>

          {/* Timer */}
          <div className="mt-4 text-center">
            <p className="text-sm text-white/60 mb-2">Tempo rimanente</p>
            <div className="text-3xl font-mono font-bold text-amber-400">
              {phase >= 4 ? '00:00:00' : '02:34:18'}
            </div>
          </div>
        </div>

        {/* Bidding Panel */}
        <div className="space-y-4">
          {/* Bid History */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[#0071e3]" />
              Storico Offerte
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {bids.map((bid, index) => (
                <div 
                  key={index}
                  className={`flex justify-between items-center p-3 rounded-lg transition-all ${
                    bid.isYou && phase >= 4 ? 'bg-[#0071e3]/20 border border-[#0071e3]/30' : 'bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      bid.isYou ? 'bg-[#0071e3]' : 'bg-white/20'
                    }`}>
                      {bid.user[0]}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${bid.isYou ? 'text-[#0071e3]' : 'text-white'}`}>
                        {bid.user}
                      </p>
                      <p className="text-xs text-white/50">{bid.time}</p>
                    </div>
                  </div>
                  <span className="font-bold text-white">€ {bid.amount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Your Bid */}
          <div className={`bg-gradient-to-r from-[#0071e3]/20 to-purple-500/20 border border-[#0071e3]/30 rounded-2xl p-4 transition-all ${
            phase >= 4 ? 'ring-2 ring-emerald-500' : ''
          }`}>
            {phase >= 4 ? (
              <div className="text-center py-4">
                <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-3" />
                <p className="text-xl font-bold text-emerald-400">Hai vinto la gara!</p>
                <p className="text-white/70 mt-2">La tua offerta di € 305.000 è stata accettata</p>
              </div>
            ) : (
              <>
                <h4 className="font-semibold text-white mb-3">La tua offerta</h4>
                <div className="flex gap-3">
                  <div className="flex-1 bg-white/10 rounded-xl p-3">
                    <span className="text-2xl font-bold text-white">€ {(bidAmount + 5000).toLocaleString()}</span>
                  </div>
                  <button className={`px-6 bg-[#0071e3] rounded-xl font-semibold transition-all ${
                    phase >= 3 ? 'animate-pulse' : ''
                  }`}>
                    Rilancia
                  </button>
                </div>
                <p className="text-xs text-white/50 mt-2">Rilancio minimo: € 5.000</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
